import React, { useState } from 'react';
import type { CanvasBlock } from '../../forms/types';
import { CanvasBlockContent } from '../../forms/formBuilder/blockRenders';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';

export interface FormBuilderCanvasBlockProps {
  block: CanvasBlock;
  index: number;
  total: number;
  selected: boolean;
  isBeingDragged: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  onDuplicate: () => void;
  onBlockDragStart: (e: React.DragEvent) => void;
  onBlockDragEnd: () => void;
}

export function FormBuilderCanvasBlock({
  block,
  index,
  total,
  selected,
  isBeingDragged,
  onSelect,
  onRemove,
  onMove,
  onDuplicate,
  onBlockDragStart,
  onBlockDragEnd,
}: FormBuilderCanvasBlockProps) {
  const [hover, setHover] = useState(false);
  const showToolbar = selected || hover;

  return (
    <div
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
      onClick={onSelect}
      draggable
      onDragStart={onBlockDragStart}
      onDragEnd={onBlockDragEnd}
      className={cn(
        'fm-builder-block',
        selected && 'is-selected',
        isBeingDragged && 'is-dragging',
        hover && !selected && 'hover:bg-[var(--cream-0)]',
      )}
    >
      {showToolbar ? (
        <div
          className="fm-builder-block-toolbar"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <ToolBtn title="Drag to reorder" passive>
            <Icon kind="grip" size={12} />
          </ToolBtn>
          <div className="mx-0.5 h-4 w-px bg-[var(--cream-2)]" />
          <ToolBtn title="Move up" disabled={index === 0} onClick={() => { onMove(-1); }}>
            <Icon kind="up" size={12} />
          </ToolBtn>
          <ToolBtn title="Move down" disabled={index === total - 1} onClick={() => { onMove(1); }}>
            <Icon kind="down" size={12} />
          </ToolBtn>
          <ToolBtn title="Duplicate" onClick={onDuplicate}>
            <Icon kind="copy" size={12} />
          </ToolBtn>
          <ToolBtn title="Delete" tone="danger" onClick={onRemove}>
            <Icon kind="trash" size={12} />
          </ToolBtn>
        </div>
      ) : null}

      <CanvasBlockContent block={block} />
    </div>
  );
}

function ToolBtn({
  children,
  onClick,
  disabled,
  title,
  tone,
  passive,
}: {
  children?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  title: string;
  tone?: 'danger';
  passive?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled || passive}
      onClick={disabled || passive ? undefined : onClick}
      className={cn(
        'fm-builder-tool-btn',
        tone === 'danger' && 'is-danger',
        passive && 'is-passive',
      )}
    >
      {children}
    </button>
  );
}
